/**
 * PayoutDetailDrawer (Plan 20).
 *
 * Right-side detail drawer opened from the history table OR from the
 * "View details" affordance on the next-payout card. Shows:
 *
 *   - Summary header (status chip + cycle date + total + currency)
 *   - Lines grouped by `kind` (ITEM → CARVE_OUT → ADJUSTMENT →
 *     CLAWBACK) with per-group subtotals
 *   - Each line's SOURCE ITEM + amount. The line's own `description` is a
 *     grouped label — every swept ITEM line reads "Kitchen pay", which is all
 *     Tipalti needs and nothing a chef (or support reading over their
 *     shoulder) can reconcile. The server resolves the item behind each line
 *     and the row leads with that name, campus and delivery date; the
 *     description is the fallback for lines with no source item.
 *
 * NO per-line deep link into /orders/kitchen. The Plan-20 spec called for
 * one (`?orderItemId=<id>`), and it shipped, but it never worked: the chef
 * orders page selects by `?select=<deliverable id>`, and a deliverable is an
 * AGGREGATED package keyed by a synthetic client-side rowKey
 * (`mealtime|temperature|fieldTrip|bulk|packaging|dryRun|identitySet` — see
 * `lib/chefs/shipment-to-campus.ts`), not a database id. An `OrderItem.id`
 * therefore has no target to match, and the projection never carried one.
 * The link rendered, navigated, and silently selected nothing.
 *
 * Restoring it needs real work, not a param rename: the payout-line payload
 * would have to carry the item's service date + component (both reachable via
 * `sourceOrderItem.menuItem`), and the orders page would need to resolve the
 * deliverable CONTAINING that component on that date. `?date=&view=` (added
 * with the chef date-range work) makes landing on the right day possible now.
 *
 * Edge cases handled:
 *   - Negative-amount lines (CLAWBACK) render with a destructive
 *     tone so the chef visually parses "money out".
 */

import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@ordo/ui/drawer'
import { PayoutLineGroups } from '@ordo/ui/payout-line-groups'
import { Skeleton } from '@ordo/ui/skeleton'

import {
  type KitchenPayoutDetail,
  type KitchenPayoutRow,
  useBillingKitchenPayoutDetailQuery,
} from '@/api/billing-kitchen'
import { cn } from '@/lib/utils'

import {
  formatCents,
  formatCycleDateShort,
  formatDeliveryDate,
  groupItemLinesByDelivery,
  isNegativePayout,
  type PayoutItemDateGroup,
  payoutItemRowDetail,
} from './kitchen-payouts-helpers'
import { PayoutStatusChip } from './payout-status-chip'

export interface PayoutDetailDrawerProps {
  open: boolean
  payoutId: string | null
  /** Pre-loaded row from the history table — used for the header while the detail loads. */
  preloadRow?: KitchenPayoutRow | null
  /** Pre-loaded detail — when the row IS the next-payout card, we already have lines. */
  preloadDetail?: KitchenPayoutDetail | null
  onOpenChange: (open: boolean) => void
}

export function PayoutDetailDrawer({
  open,
  payoutId,
  preloadRow,
  preloadDetail,
  onOpenChange,
}: PayoutDetailDrawerProps) {
  // Only fetch when the drawer is open AND we don't already have full
  // detail in preloadDetail. The next-payout-card opens the drawer
  // with preloadDetail already populated; the history-table opens
  // with preloadRow only and we fetch the lines.
  const shouldFetch = open && payoutId !== null && preloadDetail?.payout.id !== payoutId
  const { data, isPending, error } = useBillingKitchenPayoutDetailQuery(shouldFetch ? payoutId : null)

  const detail: KitchenPayoutDetail | null = data ?? (preloadDetail?.payout.id === payoutId ? preloadDetail : null)
  const header = detail?.payout ?? preloadRow ?? null

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction="right">
      <DrawerContent className="ml-auto h-full w-full max-w-[640px] rounded-none border-l">
        <DrawerHeader className="border-b">
          <div className="flex items-start justify-between gap-3">
            <div className="flex flex-col gap-1">
              <DrawerTitle>{header ? `${formatCycleDateShort(header.cycleDate)} payout` : 'Payout detail'}</DrawerTitle>
              <DrawerDescription>
                {header ? (
                  <>
                    Payout Total{' '}
                    <span className={cn('font-medium tabular-nums', isNegativePayout(header) && 'text-destructive')}>
                      {formatCents(header.totalCents, header.currency)}
                    </span>
                  </>
                ) : null}
              </DrawerDescription>
            </div>
            {header ? <PayoutStatusChip status={header.status} title={null} /> : null}
          </div>
        </DrawerHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isPending && !detail ? (
            <DetailSkeleton />
          ) : error ? (
            <p className="text-sm text-destructive">{error.message}</p>
          ) : detail ? (
            <DetailBody detail={detail} />
          ) : null}
        </div>
      </DrawerContent>
    </Drawer>
  )
}

function DetailBody({ detail }: { detail: KitchenPayoutDetail }) {
  const itemGroups = groupItemLinesByDelivery(detail.lines)
  const serviceFees = detail.lines.filter((line) => line.kind === 'VENDOR_SERVICE_FEE')
  return (
    <div className="flex flex-col gap-6">
      {itemGroups.length > 0 ? (
        <ItemsSection groups={itemGroups} currency={detail.payout.currency} />
      ) : (
        <p className="text-sm text-muted-foreground">No paid items delivered for this payout.</p>
      )}
      {serviceFees.length > 0 ? (
        <section className="flex flex-col gap-2">
          <div className="flex justify-between text-sm font-medium">
            <h3>Service fees</h3>
            <span>
              {formatCents(
                serviceFees.reduce((sum, line) => sum + line.amountCents, 0),
                detail.payout.currency,
              )}
            </span>
          </div>
          {serviceFees.map((line) => (
            <div key={line.id} className="flex justify-between gap-4 text-sm text-muted-foreground">
              <span>{line.description}</span>
              <span className="shrink-0 tabular-nums">{formatCents(line.amountCents, detail.payout.currency)}</span>
            </div>
          ))}
        </section>
      ) : null}
    </div>
  )
}

/**
 * The ITEMS this payout covers — name, servings, campus — grouped by the day
 * the food reached the school.
 *
 * This is the section the chef (and support viewing as them) actually reads.
 * Without it the drawer is hundreds of rows all labelled "Kitchen pay": the
 * September 4 Portland Catering envelope is 830 lines, which fold to 82 rows
 * here; the largest envelope in production is 6,221 lines and folds to 401.
 */
function ItemsSection({ groups, currency }: { groups: PayoutItemDateGroup[]; currency: string }) {
  const totalRows = groups.reduce((sum, group) => sum + group.rows.length, 0)
  const totalServings = groups.reduce((sum, group) => sum + (group.quantity ?? 0), 0)
  return (
    <section className="flex flex-col gap-2">
      <div className="flex flex-col gap-1">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Items delivered</h3>
        <p className="text-xs text-muted-foreground">
          {totalRows} {totalRows === 1 ? 'item' : 'items'} · {totalServings.toLocaleString()} servings across{' '}
          {groups.length} {groups.length === 1 ? 'delivery date' : 'delivery dates'}
        </p>
      </div>
      <PayoutLineGroups
        formatAmount={(cents) => formatCents(cents, currency)}
        groups={groups.map((group) => ({
          key: group.deliveryDate ?? 'unresolved',
          label: formatDeliveryDate(group.deliveryDate),
          quantity: group.quantity,
          amountCents: group.amountCents,
          rows: group.rows.map((row) => ({
            key: row.key,
            name: row.itemName,
            detail: payoutItemRowDetail(row, group.deliveryDate),
            badge: row.isDryRun ? 'Dry run' : null,
            quantity: row.quantity,
            amountCents: row.amountCents,
          })),
        }))}
      />
    </section>
  )
}

function DetailSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      {Array.from({ length: 1 }).map((_, i) => (
        <div key={i} className="flex flex-col gap-2">
          <div className="flex items-baseline justify-between">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-4 w-20" />
          </div>
          <Skeleton className="h-24 w-full rounded-md" />
        </div>
      ))}
    </div>
  )
}
